import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { cloneDeep } from 'lodash';
import { UserProfileService } from '../../user-profile/user-profile.service';
import { PatientCacheService } from './patient-cache.services';
import { Patient } from './patient.models';
import {
    convertPageToPagination,
    IPatient,
    IPatientCreateRequest,
    IPatientFilter,
    IPatientListResponse,
    IPatientPagination,
    IPatientUpdateRequest,
    PatientGender
} from './patient.types';

/**
 * Patient service refactored with Angular signals
 * Updated to handle Spring Data pagination format
 * Uses UserService to get requesterId automatically
 */
@Injectable({ providedIn: 'root' })
export class PatientService {
    private _httpClient = inject(HttpClient);
    private _userProfileService = inject(UserProfileService)

    // Main state with signals
    private _allPatients = signal<Patient[]>([]);
    private _currentPatient = signal<Patient | null>(null);
    private _isLoading = signal<boolean>(false);
    private _error = signal<string | null>(null);
    // Default filter with alphabetical sort
    private _currentFilter = signal<IPatientFilter>({ sort: 'name:asc' });
    private _pagination = signal<IPatientPagination | null>(null);

    // Public readonly signals
    public readonly patients = this._allPatients.asReadonly();
    public readonly currentPatient = this._currentPatient.asReadonly();
    public readonly isLoading = this._isLoading.asReadonly();
    public readonly error = this._error.asReadonly();
    public readonly currentFilter = this._currentFilter.asReadonly();
    public readonly pagination = this._pagination.asReadonly();

    // Computed signals for derived state
    public readonly filteredPatients = computed<Patient[]>(() => {
        const patients = this._allPatients();
        const filter = this._currentFilter();
        
        let filteredList = patients;

        // Apply filters
        if (filter.search || filter.f_gender) {
            filteredList = patients.filter(patient => {
                // Gender filter
                if (filter.f_gender && patient.gender !== filter.f_gender) {
                    return false;
                }

                // Search filter
                if (filter.search && !patient.matchesSearch(filter.search)) {
                    return false;
                }

                return true;
            });
        }

        // Apply sorting
        if (filter.sort) {
            filteredList = this.sortPatients(filteredList, filter.sort);
        }

        return filteredList;
    });

    public readonly patientStats = computed(() => {
        const patients = this._allPatients();
        const total = patients.length;
        const maleCount = patients.filter(p => p.gender === 'MALE').length;
        const femaleCount = patients.filter(p => p.gender === 'FEMALE').length;
        const otherCount = patients.filter(p => p.gender === 'OTHER').length;
        
        // Calculate average age
        const ages = patients.map(p => p.age).filter(age => age > 0);
        const averageAge = ages.length > 0 ? ages.reduce((sum, age) => sum + age, 0) / ages.length : 0;

        return {
            totalPatients: total,
            maleCount,
            femaleCount,
            otherCount,
            averageAge: Math.round(averageAge * 10) / 10
        };
    });

    public readonly hasPatients = computed(() => this._allPatients().length > 0);

    private _patientCacheService = inject(PatientCacheService);

    constructor() {
        // Load initial patients on service initialization with default sort
        this.loadPatients({ sort: 'name:asc' });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private helper methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get current requester ID from user service
     */
    // private async getOwnerId(): Promise<string> {
    //     const user = this._userService.user();
    //     if (user?.id) {
    //         return user.id;
    //     }

    //     // If not loaded, try to load it
    //     try {
    //         const userData = await this._userService.get();
    //         if (userData?.id) {
    //             return userData.id;
    //         }
    //     } catch (error) {
    //         console.warn('Could not load user data:', error);
    //     }

    //     throw new Error('Requester ID not available - user not loaded');
    // }

    private async getOwnerId(): Promise<string> {
        // Try to get from already loaded profile first
        const clinicianProfile = this._userProfileService.clinicianProfile();
        if (clinicianProfile?.entityId) {
            return clinicianProfile.entityId;
        }

        // If not loaded, fetch the profile
        try {
            const profile = await this._userProfileService.getProfile();
            if (profile?.entityId) {
                return profile.entityId;
            }
        } catch (error) {
            console.warn('Could not load clinician profile:', error);
        }

        throw new Error('Owner ID not available - clinician profile not loaded');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public API
    // -----------------------------------------------------------------------------------------------------

    /**
     * Loads patients with optional filtering and pagination
     */
    async loadPatients(filter: IPatientFilter = {}): Promise<void> {
        // Merge with default filter to preserve sorting
        const mergedFilter = { 
            sort: 'name:asc', // Default sort
            ...filter 
        };
        
        this._isLoading.set(true);
        this._error.set(null);
    
        try {
            const requesterId = await this.getOwnerId();

            // Build query parameters
            let params = new HttpParams();
            
            if (mergedFilter.search) {
                params = params.set('search', mergedFilter.search);
            }
            
            if (mergedFilter.f_gender) {
                params = params.set('f_gender', mergedFilter.f_gender);
            }
            
            if (mergedFilter.sort) {
                params = params.set('sort', mergedFilter.sort);
            }
            
            // Convert to 0-based page number for backend
            if (mergedFilter.page !== undefined) {
                params = params.set('page', (mergedFilter.page).toString());
            }
            
            if (mergedFilter.per_page !== undefined) {
                params = params.set('per_page', mergedFilter.per_page.toString());
            }
    
            const finalUrl = `aplt_backend/api/requesters/${requesterId}/patients`;
    
            const response = await this._httpClient
                .get<IPatientListResponse>(finalUrl, { params })
                .toPromise();
    
            if (response) {
                // Extract patients from Spring Data Page response
                let patients = response.content?.map(item => new Patient(item)) || [];
                
                // Apply client-side sorting if backend doesn't handle it properly
                if (mergedFilter.sort) {
                    patients = this.sortPatients(patients, mergedFilter.sort);
                }
                
                this._allPatients.set(patients);
                
                // Convert Spring Data Page to frontend pagination format
                const pagination = convertPageToPagination(response);
                this._pagination.set(pagination);
                this._currentFilter.set(mergedFilter);
            }
        } catch (error) {
            console.error('Error loading patients:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to load patients');
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Gets a specific patient by ID
     */
    async getPatientById(id: string): Promise<Patient | null> {
        // First check if patient is already loaded
        const existingPatient = this._allPatients().find(p => p.id === id || p.entityId === id);
        if (existingPatient) {
            this._currentPatient.set(cloneDeep(existingPatient));
            return existingPatient;
        }

        // If not found, load from API
        this._isLoading.set(true);
        this._error.set(null);

        try {
            const requesterId = await this.getOwnerId();
            const response = await this._httpClient
                .get<IPatient>(`aplt_backend/api/requesters/${requesterId}/patients/${id}`)
                .toPromise();

            if (response) {
                const patient = new Patient(response);
                this._currentPatient.set(cloneDeep(patient));
                return patient;
            }
            
            return null;
        } catch (error) {
            console.error('Error loading patient:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to load patient');
            return null;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Sets the current patient for details view
     */
    setCurrentPatient(patient: Patient | null): void {
        this._currentPatient.set(patient ? cloneDeep(patient) : null);
    }

    /**
     * Creates a new patient
     */
    async createPatient(patientData: IPatientCreateRequest): Promise<Patient> {
        console.log('Creating patient:', patientData.lastName, patientData.firstName);
        this._isLoading.set(true);
        this._error.set(null);
    
        try {
            const requesterId = await this.getOwnerId();
            const response = await this._httpClient
                .post<IPatient>(`aplt_backend/api/requesters/${requesterId}/patients`, patientData)
                .toPromise();
    
            if (!response) {
                throw new Error('No response received from server');
            }
    
            const newPatient = new Patient(response);
            console.log('Patient created successfully:', newPatient.fullName);
    
            // Update cache with new patient
            this._patientCacheService.setPatient(newPatient);
    
            // Reload patients to get the updated list
            await this.loadPatients(this._currentFilter());
    
            return newPatient;
        } catch (error) {
            console.error('Error creating patient:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to create patient');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Updates an existing patient
     */
    async updatePatient(id: string, patientData: IPatientUpdateRequest): Promise<Patient> {
        console.log('Updating patient:', id, patientData.lastName);
        this._isLoading.set(true);
        this._error.set(null);
    
        try {
            const requesterId = await this.getOwnerId();
            const response = await this._httpClient
                .put<IPatient>(`aplt_backend/api/requesters/${requesterId}/patients/${id}`, patientData)
                .toPromise();
    
            if (!response) {
                throw new Error('No response received from server');
            }
    
            const updatedPatient = new Patient(response);
            console.log('Patient updated successfully:', updatedPatient.fullName);
    
            // Update cache with fresh patient data
            this._patientCacheService.setPatient(updatedPatient);
    
            // Update current patient if it's the one being updated
            if (this._currentPatient()?.id === id || this._currentPatient()?.entityId === id) {
                this._currentPatient.set(cloneDeep(updatedPatient));
            }
    
            // Reload patients to get the updated list
            await this.loadPatients(this._currentFilter());
    
            return updatedPatient;
        } catch (error) {
            console.error('Error updating patient:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to update patient');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Deletes a patient
     */
    async deletePatient(id: string): Promise<boolean> {
        console.log('Deleting patient:', id);
        this._isLoading.set(true);
        this._error.set(null);
    
        try {
            const requesterId = await this.getOwnerId();
            await this._httpClient
                .post(`aplt_backend/api/requesters/${requesterId}/patients/${id}/archive`, {})
                .toPromise();
    
            console.log('Patient deleted successfully');
    
            // Remove from cache
            this._patientCacheService.removePatient(id);
    
            // Clear current patient if it's being deleted
            const currentPatient = this._currentPatient();
            if (currentPatient && (currentPatient.id === id || currentPatient.entityId === id)) {
                this._currentPatient.set(null);
            }
    
            // Reload patients to get the updated list
            await this.loadPatients(this._currentFilter());
    
            return true;
        } catch (error) {
            console.error('Error deleting patient:', error);
            // this._error.set(error instanceof Error ? error.message : 'Failed to delete patient');
            throw error;
        } finally {
            this._isLoading.set(false);
        }
    }

    /**
     * Searches patients with debounced API call
     */
    async searchPatients(searchTerm: string, gender?: PatientGender): Promise<void> {        
        const filter: IPatientFilter = {
            ...this._currentFilter(),
            search: searchTerm || undefined,
            f_gender: gender || undefined,
            page: 1 // Reset to first page on new search
        };
        
        await this.loadPatients(filter);
    }

    /**
     * Applies filter and loads patients
     */
    async applyFilter(filter: IPatientFilter): Promise<void> {
        const newFilter = { 
            sort: 'name:asc', // Maintain default sort
            ...this._currentFilter(), 
            ...filter 
        };
        await this.loadPatients(newFilter);
    }

    /**
     * Clears all filters and reloads patients with default sort
     */
    async clearFilters(): Promise<void> {
        await this.loadPatients({ sort: 'name:asc' });
    }

    /**
     * Change sort order method
     */
    async setSortOrder(sortField: string): Promise<void> {
        const filter: IPatientFilter = {
            ...this._currentFilter(),
            sort: sortField
        };
        await this.loadPatients(filter);
    }

    /**
     * Loads next page of patients
     */
    async loadNextPage(): Promise<void> {
        const currentFilter = this._currentFilter();
        const pagination = this._pagination();
        
        if (pagination?.hasNext) {
            const nextPage = pagination.currentPage + 1;
            await this.loadPatients({ ...currentFilter, page: nextPage });
        }
    }

    /**
     * Loads previous page of patients
     */
    async loadPreviousPage(): Promise<void> {
        const currentFilter = this._currentFilter();
        const pagination = this._pagination();
        
        if (pagination?.hasPrevious) {
            const prevPage = pagination.currentPage - 1;
            await this.loadPatients({ ...currentFilter, page: prevPage });
        }
    }

    /**
     * Refreshes the current patient list
     */
    async refreshPatients(): Promise<void> {
        await this.loadPatients(this._currentFilter());
    }

    /**
     * Clears all patient data (for logout)
     */
    clearData(): void {
        this._allPatients.set([]);
        this._currentPatient.set(null);
        this._error.set(null);
        this._isLoading.set(false);
        this._currentFilter.set({ sort: 'name:asc' });
        this._pagination.set(null);
    }

    /**
     * Validates patient data
     */
    validatePatient(patientData: IPatientCreateRequest | IPatientUpdateRequest): {
        isValid: boolean;
        errors: { [key: string]: string };
    } {
        const errors: { [key: string]: string } = {};

        // Required fields validation
        if (!patientData.lastName?.trim()) {
            errors.lastName = 'Last name is required';
        }

        if (!patientData.birthDate) {
            errors.birthDate = 'Birth date is required';
        } else {
            const birthDate = new Date(patientData.birthDate);
            const today = new Date();
            if (birthDate > today) {
                errors.birthDate = 'Birth date cannot be in the future';
            }
        }

        // Emergency contact required fields
        if (!patientData.emergencyContactName?.trim()) {
            errors.emergencyContactName = 'Emergency contact name is required';
        }

        if (!patientData.emergencyContactPhone?.trim()) {
            errors.emergencyContactPhone = 'Emergency contact phone is required';
        }

        // Email validation
        if (patientData.email && !this.isValidEmail(patientData.email)) {
            errors.email = 'Invalid email format';
        }

        if (patientData.emergencyContactEmail && !this.isValidEmail(patientData.emergencyContactEmail)) {
            errors.emergencyContactEmail = 'Invalid emergency contact email format';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Email validation helper
     */
    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Formats phone number for display
     */
    private formatPhoneNumber(phone: string): string {
        // Basic phone formatting - can be enhanced based on locale
        return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }

    /**
     * Calculates age from birth date
     */
    private calculateAge(birthDate: string): number {
        if (!birthDate) return 0;
        
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        
        return Math.max(0, age);
    }

    /**
     * Improved sorting method
     * Sorts patients by specified field
     */
    private sortPatients(patients: Patient[], sortField: string): Patient[] {
        if (!sortField) return patients;
        
        const [field, direction] = sortField.split(':');
        const isDesc = direction === 'desc';
        
        return [...patients].sort((a, b) => {
            let aValue: any = '';
            let bValue: any = '';
            
            switch (field) {
                case 'name':
                    // Case-insensitive and accent-insensitive sorting
                    aValue = a.fullName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    bValue = b.fullName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    break;
                case 'firstName':
                    aValue = (a.firstName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    bValue = (b.firstName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    break;
                case 'lastName':
                    aValue = a.lastName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    bValue = b.lastName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    break;
                case 'age':
                    aValue = a.age;
                    bValue = b.age;
                    break;
                case 'gender':
                    aValue = a.gender;
                    bValue = b.gender;
                    break;
                case 'city':
                    aValue = (a.city || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    bValue = (b.city || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    break;
                case 'created':
                    aValue = new Date(a.createdAt);
                    bValue = new Date(b.createdAt);
                    break;
                case 'modified':
                    aValue = new Date(a.modifiedAt);
                    bValue = new Date(b.modifiedAt);
                    break;
                default:
                    return 0;
            }
            
            // Handle null/undefined values
            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';
            
            if (aValue < bValue) return isDesc ? 1 : -1;
            if (aValue > bValue) return isDesc ? -1 : 1;
            return 0;
        });
    }
}